import * as XLSX from 'xlsx';
import {
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
  TEMPLATE_HEADERS,
  TEMPLATE_SAMPLE_DATA,
  EXCEL_COLUMN_WIDTHS,
  TEMPLATE_FILENAMES,
} from '../constants/importDialog';

/**
 * Validate if a file is a valid import file (CSV or Excel)
 * @param {File} file - File object to validate
 * @returns {boolean} True if file is valid
 */
export const isValidImportFile = (file) => {
  if (!file) return false;

  // Check file extension
  const fileExtension = file.name.toLowerCase().split('.').pop();
  const isValidExtension = ACCEPTED_FILE_EXTENSIONS.includes(`.${fileExtension}`);

  // Check MIME type
  const isValidMimeType = ACCEPTED_MIME_TYPES.includes(file.type);

  return isValidExtension || isValidMimeType;
};

/**
 * Generate CSV template content
 * @returns {string} CSV template string
 */
export const generateCSVTemplate = () => {
  const headers = TEMPLATE_HEADERS.join(',');
  const rows = TEMPLATE_SAMPLE_DATA.map((row) => {
    // Handle array values (like Projects) by wrapping in quotes
    return row.map((cell) => {
      if (Array.isArray(cell)) {
        return `"${cell.join(', ')}"`;
      }
      return cell;
    }).join(',');
  });

  return [headers, ...rows].join('\n');
};

/**
 * Generate Excel template workbook
 * @returns {Object} XLSX workbook object
 */
export const generateExcelTemplate = () => {
  // Convert sample data to array format
  const rows = TEMPLATE_SAMPLE_DATA.map((row) => {
    // Handle array values by joining them
    return row.map((cell) => {
      if (Array.isArray(cell)) {
        return cell.join(', ');
      }
      return cell;
    });
  });

  // Create worksheet with headers and sample data
  const worksheet = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...rows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');

  // Set column widths for better readability
  worksheet['!cols'] = EXCEL_COLUMN_WIDTHS;

  return workbook;
};

/**
 * Download a file from a blob
 * @param {Blob} blob - Blob object to download
 * @param {string} filename - Filename for the download
 */
export const downloadFile = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Download CSV template file
 */
export const downloadCSVTemplate = () => {
  const template = generateCSVTemplate();
  const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
  downloadFile(blob, TEMPLATE_FILENAMES.CSV);
};

/**
 * Download Excel template file
 */
export const downloadExcelTemplate = () => {
  const workbook = generateExcelTemplate();
  XLSX.writeFile(workbook, TEMPLATE_FILENAMES.EXCEL);
};
